import React, { useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import {
  Check,
  X,
  Zap,
  ShieldCheck,
  Building2,
  User,
  Sparkles,
  ArrowRight,
  Code2,
  FileText,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Globe,
  Terminal,
  Bot,
  CreditCard,
  PhoneCall,
  Award,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { upgradeUserPlan } from "../api/auth";

interface LayoutContext {
  theme?: "dark" | "light";
  toggleTheme?: () => void;
}

export default function SubscriptionPage() {
  const context = useOutletContext<LayoutContext>();
  const { user, refreshUser } = useAuth();
  // Detect theme from context or body class
  const isLight =
    context?.theme === "light" ||
    (typeof document !== "undefined" && document.body.classList.contains("light-theme"));

  const [isAnnual, setIsAnnual] = useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [upgrading, setUpgrading] = useState(false);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleChoosePlan = (planName: string) => {
    if (planName === "Enterprise") {
      setSelectedPlanForModal("Enterprise");
    } else {
      setSelectedPlanForModal(planName);
      toast.success(`You selected the ${planName} plan!`, {
        description: isAnnual ? "Billed annually with 20% discount applied." : "Billed monthly.",
      });
    }
  };

  const confirmUpgrade = async () => {
    if (!selectedPlanForModal) return;
    setUpgrading(true);
    try {
      await upgradeUserPlan(selectedPlanForModal);
      await refreshUser();
      toast.success(`Successfully activated ${selectedPlanForModal}!`, {
        description: selectedPlanForModal === "Operator / Basic"
          ? "You are now on the Free tier."
          : "Threat Intelligence API access and Exclusive Reports are now unlocked.",
      });
      setSelectedPlanForModal(null);
    } catch (err: any) {
      toast.error("Failed to update subscription. Please try again.");
    } finally {
      setUpgrading(false);
    }
  };

  const faqs = [
    {
      q: "How does the Operator / Basic Plan report download fee ($15) work?",
      a: "On the Basic Plan, account access and live threat feeds are completely free. If you need a comprehensive, branded PDF or DOCX report for an incident or intelligence digest, you can purchase individual report downloads for $15 per report directly from the Threat Feed dashboard."
    },
    {
      q: "Can I upgrade or downgrade my Threatactix plan at any time?",
      a: "Yes! You can upgrade to Business Pro or Enterprise at any time to instantly unlock unlimited report downloads, API keys, and automated alerting. Downgrades take effect at the end of your current billing cycle."
    },
    {
      q: "What API formats are supported in Business Pro & Enterprise?",
      a: "Threatactix provides REST APIs with JSON payloads, as well as STIX 2.1 and TAXII 2.1 feeds for direct integration into your SIEM, SOAR, or Splunk instances."
    },
    {
      q: "What is the Local AI Tagging Engine in the Enterprise tier?",
      a: "Enterprise customers get access to our local LLM-powered enrichment engine that categorizes, tags, and extracts IOCs (IPs, hashes, leaked PII) directly within your isolated tenant without sharing telemetry."
    },
    {
      q: "Are payment modes like UPI, Credit Cards, and Invoicing supported?",
      a: "We support Credit/Debit Cards, Net Banking, UPI (for INR payments), and corporate invoicing / PO purchases for Enterprise accounts."
    }
  ];

  return (
    <div
      className={`min-h-screen py-8 px-6 font-sans transition-colors duration-200 ${isLight ? "bg-[#f4f6fa] text-slate-900" : "bg-[#080b12] text-slate-100"
        }`}
    >
      {/* Centered Main Page Container */}
      <div className="mx-auto w-full max-w-7xl">

        {/* Hero Section */}
        <div className="text-center space-y-4 max-w-3xl mx-auto pt-2">
          <div
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border shadow-sm ${isLight
                ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                : "bg-indigo-500/10 border-indigo-500/30 text-indigo-400"
              }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-indigo-500 animate-pulse" />
            <span>Threatactix Pricing & Subscription</span>
          </div>

          <h1
            className={`text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight ${isLight ? "text-slate-900" : "text-white"
              }`}
          >
            Scalable Threat Intelligence <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
              Built for Modern SOC Operations
            </span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Monitor dark web leaks, ransomware activities, and domain threats. Choose a plan tailored to your security requirements.
          </p>
        </div>

        {/* Billing Cycle Toggle in its own centered row with mb-10 */}
        <div className="pt-6 mb-10 flex items-center justify-center gap-3">
          <span
            className={`text-sm font-medium ${!isAnnual
                ? isLight
                  ? "text-slate-900 font-bold"
                  : "text-white font-bold"
                : "text-slate-500 dark:text-slate-400"
              }`}
          >
            Monthly Billing
          </span>

          <button
            type="button"
            onClick={() => setIsAnnual(!isAnnual)}
            className={`relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isLight ? "bg-slate-300" : "bg-slate-800"
              }`}
            style={{ backgroundColor: isAnnual ? "#6366F1" : undefined }}
          >
            <span className="sr-only">Toggle annual billing</span>
            <span
              className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${isAnnual ? "translate-x-7" : "translate-x-0"
                }`}
            />
          </button>

          <span
            className={`text-sm font-medium flex items-center gap-2 ${isAnnual
                ? isLight
                  ? "text-slate-900 font-bold"
                  : "text-white font-bold"
                : "text-slate-500 dark:text-slate-400"
              }`}
          >
            Annual Billing
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${isLight
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
                }`}
            >
              Save 20%
            </span>
          </span>
        </div>

        {/* API-as-a-Service Developer Banner */}
        <div className={`mt-6 mb-2 p-4 rounded-xl border flex flex-wrap items-center justify-between gap-4 transition-all ${isLight
            ? "bg-blue-50/70 border-blue-200 shadow-sm"
            : "bg-gradient-to-r from-blue-950/40 via-indigo-950/30 to-slate-900/60 border-blue-500/30"
          }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${isLight ? "bg-blue-100 text-blue-700" : "bg-blue-500/20 text-blue-400"}`}>
              <Code2 className="w-5 h-5" />
            </div>
            <div>
              <div className={`text-sm font-bold flex items-center gap-2 ${isLight ? "text-slate-900" : "text-white"}`}>
                <span>Threatactix API-as-a-Service Developer Portal</span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${isLight ? "bg-blue-100 text-blue-800 border-blue-300" : "bg-blue-500/20 text-blue-300 border-blue-500/40"
                  }`}>
                  ₹8 / Request
                </span>
              </div>
              <div className={`text-xs ${isLight ? "text-slate-600" : "text-slate-400"}`}>
                Generate cryptographically secure API keys, monitor real-time request latencies, and automate threat feeds.
              </div>
            </div>
          </div>
          <Link
            to="/api-access"
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
          >
            <span>Manage API Keys & Metering</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* 3-Column Pricing Cards Container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch pt-4">

          {/* TIER 1: Operator / Basic Plan */}
          <div
            className={`flex flex-col justify-between rounded-2xl border p-6 shadow-sm relative h-full ${isLight
                ? "bg-white border-slate-200 hover:border-slate-300"
                : "bg-slate-900 border-slate-800 hover:border-slate-700"
              }`}
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`p-3 rounded-xl border ${isLight
                      ? "bg-slate-100 border-slate-200 text-slate-700"
                      : "bg-slate-800 border-slate-700 text-slate-300"
                    }`}
                >
                  <User className="w-6 h-6" />
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${isLight
                      ? "bg-slate-100 border-slate-200 text-slate-700"
                      : "bg-slate-800 border-slate-700 text-slate-300"
                    }`}
                >
                  Free Tier
                </span>
              </div>

              <h3 className={`text-xl font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
                Operator / Basic
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-xs mt-1 min-h-[36px]">
                For individual security researchers and small teams monitoring live threat feeds.
              </p>

              <div className="mt-6 mb-6">
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-extrabold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Free
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-sm">
                    / Pay-as-you-go
                  </span>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                  No monthly subscription required
                </p>
              </div>

              {/* Pay-Per-Use Report Download Feature Callout */}
              <div
                className={`mb-6 p-3.5 rounded-xl border text-xs font-semibold flex items-start gap-2.5 shadow-sm ${isLight
                    ? "bg-amber-50 border-amber-200 text-amber-900"
                    : "bg-amber-500/10 border-amber-500/30 text-amber-300"
                  }`}
              >
                <FileText className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold uppercase tracking-wider text-[10px] text-amber-700 dark:text-amber-200">
                    Pay-Per-Use Addon
                  </div>
                  <span className="text-xs">
                    On-Demand Threat Reports:{" "}
                    <strong className="font-extrabold text-amber-800 dark:text-amber-300">
                      $15
                    </strong>{" "}
                    per report download
                  </span>
                </div>
              </div>

              <div className={`border-t pt-6 space-y-3.5 text-sm ${isLight ? "border-slate-200" : "border-slate-800"}`}>
                <div className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider mb-3">
                  Core Capabilities
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <span>Live Threat Feed Access</span>
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <span>Basic Threat Actor Directory</span>
                </div>

                <div className="flex items-start gap-3 text-amber-800 dark:text-amber-300 font-bold">
                  <CheckCircle2 className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <span>On-Demand Threat Reports ($15/report)</span>
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <span>1 User Account</span>
                </div>

                <div className="flex items-start gap-3 text-slate-400 dark:text-slate-500 line-through">
                  <XCircle className="w-4.5 h-4.5 text-slate-300 dark:text-slate-600 shrink-0 mt-0.5" />
                  <span>Threatactix API Access</span>
                </div>

                <div className="flex items-start gap-3 text-slate-400 dark:text-slate-500 line-through">
                  <XCircle className="w-4.5 h-4.5 text-slate-300 dark:text-slate-600 shrink-0 mt-0.5" />
                  <span>Automated Telegram & Email Alerts</span>
                </div>

                <div className="flex items-start gap-3 text-slate-400 dark:text-slate-500 line-through">
                  <XCircle className="w-4.5 h-4.5 text-slate-300 dark:text-slate-600 shrink-0 mt-0.5" />
                  <span>Ransomware & Vulnerability Modules</span>
                </div>
              </div>
            </div>

            {/* CTA Button Pinned to Bottom */}
            <div className="mt-auto pt-6">
              <button
                type="button"
                onClick={() => handleChoosePlan("Operator / Basic")}
                className={`w-full py-3.5 px-4 rounded-xl font-bold text-sm transition-all border flex items-center justify-center gap-2 ${isLight
                    ? "bg-slate-900 hover:bg-slate-800 text-white border-slate-900 shadow-sm"
                    : "bg-slate-800 hover:bg-slate-700 text-white border-slate-700 shadow-sm"
                  }`}
              >
                <span>Get Started Free</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* TIER 2: Business Pro (RECOMMENDED / HIGHLIGHTED) */}
          <div
            className={`flex flex-col justify-between rounded-2xl border-2 border-indigo-500 shadow-lg shadow-indigo-500/20 p-6 transition-all duration-300 relative h-full ${isLight ? "bg-white" : "bg-slate-900"
              }`}
          >
            {/* Unclipped Absolute Badge at top-right */}
            <div className="absolute -top-3 right-4 bg-indigo-600 text-white font-bold text-xs px-3.5 py-1 rounded-full shadow-md flex items-center gap-1.5 z-10">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Most Popular</span>
            </div>

            <div className="flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={`p-3 rounded-xl border ${isLight
                        ? "bg-indigo-50 border-indigo-200 text-indigo-600"
                        : "bg-indigo-500/20 border-indigo-500/40 text-indigo-400"
                      }`}
                  >
                    <Zap className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                    Recommended
                  </span>
                </div>

                <h3 className={`text-2xl font-extrabold ${isLight ? "text-slate-900" : "text-white"}`}>
                  Business Pro
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-xs mt-1 min-h-[36px]">
                  Built for businesses needing automated intelligence, API integration, and unlimited exports.
                </p>

                <div className="mt-6 mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-extrabold ${isLight ? "text-slate-900" : "text-white"}`}>
                      {isAnnual ? "$49" : "$59"}
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 text-sm font-semibold">
                      / month
                    </span>
                  </div>
                  <p className="text-indigo-600 dark:text-indigo-400 text-xs mt-1 font-semibold">
                    {isAnnual ? "Billed annually ($588/yr)" : "Billed monthly"}
                  </p>
                </div>

                {/* Highlighted Unlimited Reports Feature Callout */}
                <div
                  className={`mb-6 p-3.5 rounded-xl border text-xs font-semibold flex items-center gap-2.5 shadow-sm ${isLight
                      ? "bg-indigo-50 border-indigo-200 text-indigo-900"
                      : "bg-indigo-500/15 border-indigo-400/40 text-indigo-200"
                    }`}
                >
                  <ShieldCheck className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0" />
                  <span>Includes <strong>Unlimited Threat Report Downloads (PDF/DOCX)</strong></span>
                </div>

                <div className={`border-t pt-6 space-y-3.5 text-sm ${isLight ? "border-indigo-100" : "border-indigo-500/20"}`}>
                  <div className="text-indigo-700 dark:text-indigo-300 text-xs font-bold uppercase tracking-wider mb-3">
                    Everything in Basic, plus:
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Unlimited Threat Feed & Actor Directory</span>
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Ransomware & Vulnerability Intelligence</span>
                  </div>

                  <div className="flex items-start gap-3 text-emerald-700 dark:text-emerald-300 font-bold">
                    <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Unlimited Threat Report Downloads (PDF/DOCX)</span>
                  </div>

                  <div className="flex items-start gap-3 text-indigo-700 dark:text-cyan-300 font-bold">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Threatactix API (1,000 billable requests / month included)</span>
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Secure API-Key Management & Multi-Key Isolation</span>
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Programmatic Threat Intelligence Retrieval & Search</span>
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Real-Time Usage & P95 Request Latency Monitoring</span>
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Automated Alerts (Email & Telegram)</span>
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Up to 5 Team Member Accounts</span>
                  </div>

                  <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                    <CheckCircle2 className="w-4.5 h-4.5 text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                    <span>Priority Email & In-App Support</span>
                  </div>
                </div>
              </div>

              {/* CTA Button Pinned to Bottom */}
              <div className="mt-auto pt-6">
                <button
                  type="button"
                  onClick={() => handleChoosePlan("Business Pro")}
                  className="w-full py-3.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm transition-all shadow-md shadow-indigo-500/20 flex items-center justify-center gap-2"
                >
                  <span>Upgrade to Business Pro</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* TIER 3: Enterprise */}
          <div
            className={`flex flex-col justify-between rounded-2xl border p-6 shadow-sm relative h-full ${isLight
                ? "bg-white border-slate-200 hover:border-slate-300"
                : "bg-slate-900 border-slate-800 hover:border-slate-700"
              }`}
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`p-3 rounded-xl border ${isLight
                      ? "bg-purple-50 border-purple-200 text-purple-600"
                      : "bg-purple-500/20 border-purple-500/40 text-purple-400"
                    }`}
                >
                  <Building2 className="w-6 h-6" />
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${isLight
                      ? "bg-purple-50 border-purple-200 text-purple-700"
                      : "bg-purple-500/10 border-purple-500/30 text-purple-300"
                    }`}
                >
                  SOC & Enterprise
                </span>
              </div>

              <h3 className={`text-xl font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
                Enterprise
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-xs mt-1 min-h-[36px]">
                For large enterprises with complex SOC operations needing dedicated processing.
              </p>

              <div className="mt-6 mb-6">
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-extrabold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Custom
                  </span>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                  Tailored contract & custom invoicing
                </p>
              </div>

              <div
                className={`mb-6 p-3.5 rounded-xl border text-xs font-semibold flex items-center gap-2.5 shadow-sm ${isLight
                    ? "bg-purple-50 border-purple-200 text-purple-900"
                    : "bg-purple-500/10 border-purple-500/30 text-purple-300"
                  }`}
              >
                <Bot className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400 shrink-0" />
                <span>Includes <strong>Dedicated Local AI Engine & SIEM Connectors</strong></span>
              </div>

              <div className={`border-t pt-6 space-y-3.5 text-sm ${isLight ? "border-slate-200" : "border-slate-800"}`}>
                <div className="text-purple-700 dark:text-purple-300 text-xs font-bold uppercase tracking-wider mb-3">
                  Everything in Business Pro, plus:
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                  <span>Dedicated Local AI Tagging & Processing Engine</span>
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                  <span>Advanced Integrations (Splunk, SIEM, STIX/TAXII)</span>
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                  <span>Unlimited API Access & Custom Rate Limits</span>
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                  <span>Dedicated Account Manager & 24/7 Phone Support</span>
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                  <span>Unlimited Team Accounts & Custom RBAC</span>
                </div>

                <div className="flex items-start gap-3 text-slate-800 dark:text-slate-200 font-medium">
                  <CheckCircle2 className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                  <span>Custom SLA & On-Premise Deployment Options</span>
                </div>
              </div>
            </div>

            {/* CTA Button Pinned to Bottom */}
            <div className="mt-auto pt-6">
              <button
                type="button"
                onClick={() => handleChoosePlan("Enterprise")}
                className={`w-full py-3.5 px-4 rounded-xl font-bold text-sm transition-all border flex items-center justify-center gap-2 ${isLight
                    ? "bg-slate-900 hover:bg-slate-800 text-white border-slate-900 shadow-sm"
                    : "bg-slate-800 hover:bg-slate-700 text-white border-slate-700 shadow-sm"
                  }`}
              >
                <span>Contact Sales</span>
                <PhoneCall className="w-4 h-4" />
              </button>
            </div>
          </div>

        </div>

        {/* Platform Value Highlights Grid Section with mt-16 */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className={`text-2xl font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
              Why Threatactix?
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
              Empowering SOC teams with verified cyber threat intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div
              className={`p-6 rounded-2xl border shadow-sm space-y-3 transition-all ${isLight ? "bg-white border-slate-200 hover:shadow-md" : "bg-slate-900 border-slate-800"
                }`}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isLight
                    ? "bg-indigo-50 border-indigo-200 text-indigo-600"
                    : "bg-indigo-500/10 border-indigo-500/30 text-indigo-400"
                  }`}
              >
                <Globe className="w-5 h-5" />
              </div>
              <h4 className={`font-bold text-base ${isLight ? "text-slate-900" : "text-white"}`}>
                Dark Web Monitoring
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                Automated continuous indexing of cybercrime forums, Telegram channels, and breach markets.
              </p>
            </div>

            <div
              className={`p-6 rounded-2xl border shadow-sm space-y-3 transition-all ${isLight ? "bg-white border-slate-200 hover:shadow-md" : "bg-slate-900 border-slate-800"
                }`}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isLight
                    ? "bg-cyan-50 border-cyan-200 text-cyan-700"
                    : "bg-cyan-500/10 border-cyan-500/30 text-cyan-400"
                  }`}
              >
                <FileText className="w-5 h-5" />
              </div>
              <h4 className={`font-bold text-base ${isLight ? "text-slate-900" : "text-white"}`}>
                Executive Threat Reports
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                Export battle-tested PDF and DOCX threat reports ready for CISO and board-level presentation.
              </p>
            </div>

            <div
              className={`p-6 rounded-2xl border shadow-sm space-y-3 transition-all ${isLight ? "bg-white border-slate-200 hover:shadow-md" : "bg-slate-900 border-slate-800"
                }`}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isLight
                    ? "bg-purple-50 border-purple-200 text-purple-700"
                    : "bg-purple-500/10 border-purple-500/30 text-purple-400"
                  }`}
              >
                <Terminal className="w-5 h-5" />
              </div>
              <h4 className={`font-bold text-base ${isLight ? "text-slate-900" : "text-white"}`}>
                STIX 2.1 & REST API
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                Seamlessly feed high-confidence IOCs into your SIEM, SOAR, and firewall rules in real time.
              </p>
            </div>

            <div
              className={`p-6 rounded-2xl border shadow-sm space-y-3 transition-all ${isLight ? "bg-white border-slate-200 hover:shadow-md" : "bg-slate-900 border-slate-800"
                }`}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isLight
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                  }`}
              >
                <Award className="w-5 h-5" />
              </div>
              <h4 className={`font-bold text-base ${isLight ? "text-slate-900" : "text-white"}`}>
                Zero False-Positive Guarantee
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                Human analyst validated alerts to eliminate noise and save precious SOC triage time.
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Feature Comparison Table Section with mt-16 */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className={`text-2xl font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
              Compare Plan Specifications
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
              Detailed breakdown of platform capabilities across all tiers.
            </p>
          </div>

          <div
            className={`overflow-x-auto rounded-2xl border shadow-sm ${isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
              }`}
          >
            <table className="w-full text-left text-sm border-collapse">
              <thead
                className={`border-b text-xs uppercase tracking-wider font-bold ${isLight
                    ? "bg-slate-100 text-slate-700 border-slate-200"
                    : "bg-slate-800 text-slate-300 border-slate-800"
                  }`}
              >
                <tr>
                  <th className="py-3.5 px-4 font-bold">Features & Capabilities</th>
                  <th className="py-3.5 px-4 font-bold text-center">Operator / Basic</th>
                  <th
                    className={`py-3.5 px-4 font-bold text-center border-x ${isLight
                        ? "bg-indigo-50 text-indigo-900 border-indigo-200"
                        : "bg-indigo-500/15 text-indigo-300 border-indigo-500/30"
                      }`}
                  >
                    Business Pro
                  </th>
                  <th className="py-3.5 px-4 font-bold text-center">Enterprise</th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${isLight ? "divide-slate-200 text-slate-700" : "divide-slate-800 text-slate-300"
                  }`}
              >
                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Monthly Subscription Price
                  </td>
                  <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400 font-medium">Free Access</td>
                  <td
                    className={`py-3 px-4 text-center font-bold border-x ${isLight
                        ? "bg-indigo-50/40 text-indigo-700 border-indigo-200/60"
                        : "bg-indigo-500/5 text-indigo-300 border-indigo-500/20"
                      }`}
                  >
                    $59 / mo
                  </td>
                  <td className="py-3 px-4 text-center text-purple-700 dark:text-purple-300 font-semibold">
                    Custom Pricing
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Threat Report Downloads
                  </td>
                  <td className="py-3 px-4 text-center text-amber-700 dark:text-amber-400 font-bold bg-amber-50/40 dark:bg-amber-500/5">
                    $15 / report
                  </td>
                  <td
                    className={`py-3 px-4 text-center text-emerald-600 dark:text-emerald-400 font-bold border-x ${isLight
                        ? "bg-indigo-50/40 border-indigo-200/60"
                        : "bg-indigo-500/5 border-indigo-500/20"
                      }`}
                  >
                    Unlimited Free
                  </td>
                  <td className="py-3 px-4 text-center text-emerald-600 dark:text-emerald-400 font-bold">
                    Unlimited Free
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Live Threat Feed Access
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                  <td
                    className={`py-3 px-4 text-center border-x ${isLight
                        ? "bg-indigo-50/40 border-indigo-200/60"
                        : "bg-indigo-500/5 border-indigo-500/20"
                      }`}
                  >
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Threat Actor Directory
                  </td>
                  <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">Basic Info</td>
                  <td
                    className={`py-3 px-4 text-center font-medium border-x ${isLight
                        ? "bg-indigo-50/40 text-indigo-900 border-indigo-200/60"
                        : "bg-indigo-500/5 text-indigo-200 border-indigo-500/20"
                      }`}
                  >
                    Full Access
                  </td>
                  <td className="py-3 px-4 text-center text-purple-700 dark:text-purple-200 font-medium">
                    Full Access + Deep Profiles
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Ransomware & Vulnerability Intelligence
                  </td>
                  <td className="py-3 px-4 text-center">
                    <X className="w-5 h-5 text-slate-300 dark:text-slate-600 mx-auto" />
                  </td>
                  <td
                    className={`py-3 px-4 text-center border-x ${isLight
                        ? "bg-indigo-50/40 border-indigo-200/60"
                        : "bg-indigo-500/5 border-indigo-500/20"
                      }`}
                  >
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    API Integration Rate Limit
                  </td>
                  <td className="py-3 px-4 text-center">
                    <X className="w-5 h-5 text-slate-300 dark:text-slate-600 mx-auto" />
                  </td>
                  <td
                    className={`py-3 px-4 text-center font-mono font-medium border-x ${isLight
                        ? "bg-indigo-50/40 text-indigo-700 border-indigo-200/60"
                        : "bg-indigo-500/5 text-indigo-300 border-indigo-500/20"
                      }`}
                  >
                    1,000 req/mo
                  </td>
                  <td className="py-3 px-4 text-center font-mono font-medium text-purple-700 dark:text-purple-300">
                    Unlimited
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Automated Alerts (Telegram / Email)
                  </td>
                  <td className="py-3 px-4 text-center">
                    <X className="w-5 h-5 text-slate-300 dark:text-slate-600 mx-auto" />
                  </td>
                  <td
                    className={`py-3 px-4 text-center border-x ${isLight
                        ? "bg-indigo-50/40 border-indigo-200/60"
                        : "bg-indigo-500/5 border-indigo-500/20"
                      }`}
                  >
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    User Accounts Included
                  </td>
                  <td className="py-3 px-4 text-center text-slate-700 dark:text-slate-300">1 User</td>
                  <td
                    className={`py-3 px-4 text-center font-semibold border-x ${isLight
                        ? "bg-indigo-50/40 text-slate-900 border-indigo-200/60"
                        : "bg-indigo-500/5 text-slate-100 border-indigo-500/20"
                      }`}
                  >
                    Up to 5 Users
                  </td>
                  <td className="py-3 px-4 text-center font-semibold text-purple-700 dark:text-purple-300">
                    Unlimited
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Local AI Enrichment Engine
                  </td>
                  <td className="py-3 px-4 text-center">
                    <X className="w-5 h-5 text-slate-300 dark:text-slate-600 mx-auto" />
                  </td>
                  <td
                    className={`py-3 px-4 text-center border-x ${isLight
                        ? "bg-indigo-50/40 border-indigo-200/60"
                        : "bg-indigo-500/5 border-indigo-500/20"
                      }`}
                  >
                    <X className="w-5 h-5 text-slate-300 dark:text-slate-600 mx-auto" />
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    SIEM / Splunk / STIX Integration
                  </td>
                  <td className="py-3 px-4 text-center">
                    <X className="w-5 h-5 text-slate-300 dark:text-slate-600 mx-auto" />
                  </td>
                  <td
                    className={`py-3 px-4 text-center border-x ${isLight
                        ? "bg-indigo-50/40 text-slate-600 border-indigo-200/60"
                        : "bg-indigo-500/5 text-slate-400 border-indigo-500/20"
                      }`}
                  >
                    Basic Webhooks
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                  </td>
                </tr>

                <tr>
                  <td className={`py-3 px-4 font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                    Support SLA
                  </td>
                  <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">Community Support</td>
                  <td
                    className={`py-3 px-4 text-center font-medium border-x ${isLight
                        ? "bg-indigo-50/40 text-indigo-900 border-indigo-200/60"
                        : "bg-indigo-500/5 text-indigo-300 border-indigo-500/20"
                      }`}
                  >
                    Standard Email / Chat
                  </td>
                  <td className="py-3 px-4 text-center text-purple-700 dark:text-purple-300 font-semibold">
                    24/7 Dedicated Account Manager
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className={`text-2xl font-bold flex items-center justify-center gap-2 ${isLight ? "text-slate-900" : "text-white"}`}>
              <HelpCircle className="w-6 h-6 text-indigo-500" />
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
              Have questions about Threatactix billing and tiers?
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`rounded-2xl border shadow-sm overflow-hidden transition-all duration-200 ${isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
                  }`}
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className={`w-full p-5 text-left flex items-center justify-between gap-4 font-semibold text-base transition-colors ${isLight
                      ? "text-slate-900 hover:text-indigo-600 hover:bg-slate-50"
                      : "text-white hover:text-indigo-400 hover:bg-slate-800/50"
                    }`}
                >
                  <span>{faq.q}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-indigo-500 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div
                    className={`px-5 pb-5 pt-3 text-sm leading-relaxed border-t ${isLight
                        ? "text-slate-600 border-slate-100"
                        : "text-slate-300 border-slate-800"
                      }`}
                  >
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Callout Banner */}
        <div className="mt-16 rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border border-slate-800 text-white p-8 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-white">Need a custom plan for your Security Operations Center?</h3>
            <p className="text-slate-300 text-sm max-w-xl leading-relaxed">
              Our threat intelligence experts can tailor custom API thresholds, STIX TAXII feeds, and on-premises deployment for your organization.
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleChoosePlan("Enterprise")}
            className="shrink-0 px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm transition-all shadow-md flex items-center gap-2"
          >
            <span>Request Custom SOC Quote</span>
            <ArrowRight className="w-4 h-4 text-white" />
          </button>
        </div>

      </div>

      {/* Plan Selection Confirmation Modal */}
      {selectedPlanForModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div
            className={`rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-6 relative border animate-in fade-in zoom-in duration-200 ${isLight ? "bg-white border-slate-200 text-slate-900" : "bg-slate-900 border-slate-800 text-white"
              }`}
          >
            <button
              onClick={() => setSelectedPlanForModal(null)}
              className={`absolute top-4 right-4 p-1.5 rounded-lg transition-colors ${isLight ? "text-slate-400 hover:text-slate-900 hover:bg-slate-100" : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div
                className={`p-3 rounded-xl border ${isLight
                    ? "bg-indigo-50 border-indigo-200 text-indigo-600"
                    : "bg-indigo-500/20 border-indigo-500/30 text-indigo-400"
                  }`}
              >
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
                  Confirm Plan Selection
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-xs">
                  Threatactix Threat Intelligence
                </p>
              </div>
            </div>

            <div
              className={`p-4 rounded-xl border space-y-3 ${isLight ? "bg-slate-50 border-slate-200" : "bg-slate-800/50 border-slate-700/60"
                }`}
            >
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500 dark:text-slate-400">Selected Plan:</span>
                <span className={`font-bold ${isLight ? "text-slate-900" : "text-white"}`}>{selectedPlanForModal}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500 dark:text-slate-400">Billing Cycle:</span>
                <span className="font-medium text-indigo-600 dark:text-indigo-300">
                  {selectedPlanForModal === "Operator / Basic"
                    ? "Pay-As-You-Go"
                    : isAnnual ? "Annual (20% Off)" : "Monthly"}
                </span>
              </div>
              <div className={`flex justify-between items-center text-sm pt-2 border-t ${isLight ? "border-slate-200" : "border-slate-700/60"}`}>
                <span className="text-slate-700 dark:text-slate-300 font-semibold">Total Amount:</span>
                <span className="font-extrabold text-emerald-600 dark:text-emerald-400 text-base">
                  {selectedPlanForModal === "Operator / Basic"
                    ? "Free ($15/report)"
                    : selectedPlanForModal === "Business Pro"
                      ? isAnnual ? "$588 / year" : "$59 / month"
                      : "Custom Quote"}
                </span>
              </div>
            </div>

            {selectedPlanForModal === "Operator / Basic" && (
              <p
                className={`text-xs p-3 rounded-lg border ${isLight
                    ? "bg-amber-50 border-amber-200 text-amber-900"
                    : "bg-amber-500/10 border-amber-500/20 text-amber-300"
                  }`}
              >
                Notice: On the Basic plan, live threat feed access is free. Threat report downloads are billed at $15 per download.
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setSelectedPlanForModal(null)}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${isLight ? "bg-slate-100 hover:bg-slate-200 text-slate-700" : "bg-slate-800 hover:bg-slate-700 text-slate-300"
                  }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmUpgrade}
                disabled={upgrading}
                className="flex-1 py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold text-sm transition-all shadow-md flex items-center justify-center gap-1.5"
              >
                <span>{upgrading ? "Activating..." : "Confirm"}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
